import { useCookieState } from 'ahooks';
import { Check, Globe } from 'lucide-react';

import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';
import {
  DEFAULT_LANGUAGE,
  languageNames,
  SUPPORTED_LANGUAGES,
} from '@shared-runtime/config/locale';
import { COOKIE_LOCALE } from '@shared-web/constants/cookies';
import { cookieDomain } from '@shared-web/modules/auth/utils/clearSession';

/**
 * Language Switcher for Webview
 *
 * Unlike the web version, this doesn't use localized routes.
 * It stores the selected language in cookie (crypto.locale) for persistence.
 */
export function LanguageSwitcher() {
  const [currentLang, setCurrentLang] = useCookieState(COOKIE_LOCALE, {
    defaultValue: DEFAULT_LANGUAGE,
    domain: cookieDomain || undefined,
  });

  const handleLanguageChange = (newLang: string) => {
    setCurrentLang(newLang);
    // Reload the page to apply language change
    window.location.reload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" className="h-9 gap-2 px-3">
          <Globe className="size-4" />
          <span className="text-body2">{currentLang}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-10000 min-w-48">
        {SUPPORTED_LANGUAGES.map((langCode) => {
          const isSelected = langCode === currentLang;
          return (
            <DropdownMenuItem
              key={langCode}
              onClick={() => handleLanguageChange(langCode)}
              className="flex cursor-pointer items-center justify-between"
            >
              <span>
                {languageNames[langCode as keyof typeof languageNames] ??
                  langCode}
              </span>
              {isSelected && <Check className="size-4" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
